// Mikrofon-Aufnahme für Anrufe (Anruf-Aufnahme + Transkription, siehe
// past-plans/ich-will-bei-jedem-logical-finch.md).
//
// Läuft absichtlich AUSSERHALB von CallCockpit/Zustand: `useTrackedDial` startet die Aufnahme,
// bevor CallCockpit (oder auch nur DialGate) überhaupt gemountet ist — sonst geht der
// Gesprächsanfang verloren. Ein eigener, kleiner Singleton mit `useSyncExternalStore` reicht für
// die einzige reaktive Anzeige, die es braucht (roter Punkt + Timer in CallCockpit), ohne den
// globalen Store anzufassen.
//
// Nimmt über das Laptop-Mikrofon auf — deckt sowohl "Anruf läuft über Laptop-Softphone" als auch
// "Anruf läuft übers Handy, Laptop-Mikro läuft nebenher mit" ab, ohne Telefonie-Anbieter. Bekannte
// Einschränkung (akzeptiert): bei Handy-Anrufen ohne Lautsprecher ist nur die Setter-Stimme klar
// hörbar.
import { useSyncExternalStore } from 'react';

type State = { isRecording: boolean; startedAt: number | null };

let state: State = { isRecording: false, startedAt: null };
const listeners = new Set<() => void>();

function setState(next: Partial<State>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

let stream: MediaStream | null = null;
let recorder: MediaRecorder | null = null;
let chunks: Blob[] = [];

// Sprachaufnahme braucht keine hohe Bitrate — niedrig gehalten, damit eine 30-Minuten-Aufnahme
// unter dem Discord-Anhangslimit (~8MB ohne Server-Boost) bleibt.
const RECORDER_OPTIONS: MediaRecorderOptions = {
  mimeType: 'audio/webm;codecs=opus',
  audioBitsPerSecond: 24_000,
};

/** Einmalig proaktiv Mikrofon-Zugriff einholen (z.B. beim Laden der Anrufen-Seite), damit die
 * Berechtigung nicht erst mitten im Anruf überraschend fehlt. Fehler werden verschluckt — der
 * Anruf-Ablauf darf davon nie abhängen. */
export async function ensureMicPermission(): Promise<boolean> {
  if (stream) return true;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    return true;
  } catch {
    return false;
  }
}

/** Startet die Aufnahme sofort (vor DialGate/CallCockpit). Kein-Op falls schon aktiv oder falls
 * keine Mikrofon-Berechtigung zu bekommen ist — der Anruf-Ablauf läuft in beiden Fällen normal
 * weiter, nur ohne Aufnahme für diesen Call. */
export async function startCallRecording(): Promise<void> {
  if (state.isRecording) return;
  try {
    if (!stream) {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    }
    chunks = [];
    let mr: MediaRecorder;
    try {
      mr = new MediaRecorder(stream, RECORDER_OPTIONS);
    } catch {
      // Manche Browser kennen den Opus-Codec-String nicht — Rückfall auf Browser-Default.
      mr = new MediaRecorder(stream);
    }
    mr.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    mr.start(1000); // sekündlich chunk-en statt einen einzigen riesigen Blob am Ende
    recorder = mr;
    setState({ isRecording: true, startedAt: Date.now() });
  } catch {
    // Keine Berechtigung / kein Mikrofon — bewusst still, siehe Docstring oben.
    recorder = null;
  }
}

/** Stoppt die Aufnahme (beim "Call speichern") und liefert den fertigen Blob, oder null wenn nie
 * aufgenommen wurde. */
export function stopCallRecording(): Promise<Blob | null> {
  if (!recorder || !state.isRecording) {
    setState({ isRecording: false, startedAt: null });
    return Promise.resolve(null);
  }
  const mr = recorder;
  return new Promise((resolve) => {
    mr.onstop = () => {
      const blob = chunks.length ? new Blob(chunks, { type: 'audio/webm' }) : null;
      chunks = [];
      recorder = null;
      setState({ isRecording: false, startedAt: null });
      resolve(blob);
    };
    try {
      mr.stop();
    } catch {
      recorder = null;
      setState({ isRecording: false, startedAt: null });
      resolve(null);
    }
  });
}

/** React-Hook für CallCockpit: liefert reaktiv, ob gerade aufgenommen wird. Die verstrichene Zeit
 * berechnet der Aufrufer selbst aus `startedAt` (eigener Timer-Tick), damit dieser Hook nicht
 * jede Sekunde re-rendert. */
export function useCallRecordingState(): State {
  return useSyncExternalStore(subscribe, () => state);
}
